import type { FC } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface KineticWordmarkProps {
    className?: string
}

const LETTERS = 'kinematic'.split('')
const NARROW = 76
const WIDE = 100

/**
 * The wordmark breathes through the font's width axis: letters near the pointer
 * stretch out, and a slow wave runs through them when nobody is touching it.
 */
const KineticWordmark: FC<KineticWordmarkProps> = ({ className }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const lettersRef = useRef<(HTMLSpanElement | null)[]>([])

    useEffect(() => {
        const container = containerRef.current
        if (!container || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const widths = LETTERS.map(() => NARROW)
        let pointer: number | null = null
        let frame = 0
        let visible = false

        const step = (now: number) => {
            frame = 0
            const time = now / 1000

            lettersRef.current.forEach((letter, index) => {
                if (!letter) return

                let target = NARROW

                if (pointer !== null) {
                    const rect = letter.getBoundingClientRect()
                    const distance = Math.abs(pointer - (rect.left + rect.width / 2))
                    target = NARROW + (WIDE - NARROW) * Math.max(0, 1 - distance / 360)
                }
                else {
                    target = NARROW + (WIDE - NARROW) * Math.pow(Math.max(0, Math.sin(time * 1.4 - index * 0.6)), 6)
                }

                widths[index] += (target - widths[index]) * 0.12
                letter.style.fontVariationSettings = `'wdth' ${widths[index].toFixed(1)}`
                letter.style.fontWeight = String(Math.round(520 + (widths[index] - NARROW) * 7))
            })

            if (visible) frame = requestAnimationFrame(step)
        }

        const onMove = (event: PointerEvent) => {
            pointer = event.clientX
        }

        const onLeave = () => {
            pointer = null
        }

        const observer = new IntersectionObserver(([entry]) => {
            visible = entry ? entry.isIntersecting : false
            if (visible && !frame) frame = requestAnimationFrame(step)
        })

        container.addEventListener('pointermove', onMove)
        container.addEventListener('pointerleave', onLeave)
        observer.observe(container)

        return () => {
            cancelAnimationFrame(frame)
            observer.disconnect()
            container.removeEventListener('pointermove', onMove)
            container.removeEventListener('pointerleave', onLeave)
        }
    }, [])

    return (
        <div ref={containerRef} aria-hidden='true' className={cn('select-none overflow-hidden', className)}>
            <div className='flex justify-center whitespace-nowrap text-[27vw] font-semibold leading-[0.8] tracking-[-0.045em] text-ink'>
                {LETTERS.map((letter, index) => (
                    <span
                        key={index}
                        ref={node => {
                            lettersRef.current[index] = node
                        }}
                        className='inline-block'
                        style={{ fontVariationSettings: `'wdth' ${NARROW}`, fontWeight: 520 }}
                    >
                        {letter}
                    </span>
                ))}
            </div>
        </div>
    )
}

export default KineticWordmark
